import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDaySheets, type DaySheet } from "../../api/routing";
import { PageHead } from "../../components/ui";
import "../../styles/routing.css";
import { tileMap } from "./tileMap";

/** One printable page per route day: the stops with what the crew needs to
 * know, the totals, and a map composited in the browser from OSM tiles. The
 * PDF button posts that same picture to the server, which draws what it is
 * given and never fetches a tile itself. */
export default function DaySheetsPage() {
  const { planId } = useParams();
  const [params] = useSearchParams();
  const day = params.get("day");
  const view = useDaySheets(Number(planId), day === null ? null : Number(day));
  if (view.isPending) {
    return <p className="app-loading">Building the day sheets…</p>;
  }
  if (view.isError) {
    return <p className="notice bad">The day sheets could not be built: {view.error.message}</p>;
  }
  return (
    <div className="daysheets">
      <div className="no-print">
        <PageHead
          title={`${view.data.plan.name} · day sheets`}
          sub="One page per day, with the map the field operators drive from."
          actions={
            <>
              <Link className="btn" to={`/routing/plans/${view.data.plan.id}`}>
                Back to the plan
              </Link>
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                Print
              </button>
            </>
          }
        />
      </div>
      {view.data.sheets.length === 0 ? <p className="empty-state card">This plan has no days to print.</p> : null}
      {view.data.sheets.map((sheet) => (
        <Sheet key={sheet.day_index} sheet={sheet} planId={view.data.plan.id} />
      ))}
    </div>
  );
}

export function Sheet({ sheet, planId }: { sheet: DaySheet; planId: number }) {
  const [png, setPng] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const data = sheet.map_data;
    if (data.line.length < 2) {
      return;
    }
    tileMap(data.line, data.markers, 720, 1040, data.color, data.approximate_line)
      .then((uri) => {
        if (!cancelled) {
          setPng(uri);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sheet]);

  return (
    <section className="sheet" aria-label={`Day ${sheet.day_number}`}>
      <header className="sheet-head" style={{ borderColor: sheet.color }}>
        <div>
          <h2>
            Day {sheet.day_number} of {sheet.total_days}
            {sheet.date_label ? ` · ${sheet.date_label}` : ""}
          </h2>
          <p>
            {sheet.contract_label} · from {sheet.origin_label}
          </p>
        </div>
        <dl className="sheet-facts">
          <dt>Sites</dt>
          <dd>{sheet.total_sites}</dd>
          <dt>Driving</dt>
          <dd>
            {sheet.total_km} km · {sheet.drive_time}
          </dd>
          <dt>Depart</dt>
          <dd>{sheet.depart_clock}</dd>
          <dt>{sheet.returns_home ? "Back" : "Last stop"}</dt>
          <dd>{sheet.return_clock}</dd>
        </dl>
        <form method="post" action={`/routing/${planId}/daysheet.pdf`} className="no-print">
          <input type="hidden" name="day" value={sheet.day_index} />
          <input type="hidden" name="map_png" value={png ?? ""} />
          <button type="submit" className="btn btn-secondary">
            Download PDF{sheet.qcp_count > 0 ? ` + ${sheet.qcp_count} QCP${sheet.qcp_count === 1 ? "" : "s"}` : ""}
          </button>
        </form>
      </header>
      <div className="sheet-body">
        <table className="data-table sheet-stops">
          <thead>
            <tr>
              <th>#</th>
              <th>Site</th>
              <th>Valve</th>
              <th>References</th>
              <th>Leg</th>
              <th>Arrive</th>
              <th>Leave</th>
              {sheet.has_actuals ? (
                <>
                  <th>Actual in</th>
                  <th>Actual out</th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {sheet.stops.map((s) => (
              <tr key={s.seq} className={s.flagged ? "flagged" : undefined}>
                <td className="num">{s.seq}</td>
                <td>
                  <b>{s.name}</b>
                  <span className="sub">{s.address ?? ""}</span>
                  <span className="sub">
                    {s.status ?? ""}
                    {s.age ? ` · ${s.age}` : ""}
                    {s.site_type ? ` · ${s.site_type}` : ""}
                    {s.has_qcp ? "" : " · no QCP"}
                    {s.coord_source ? ` · ${s.coord_source}` : ""}
                  </span>
                  {s.flag_summary ? <span className="sub flag">{s.flag_summary}</span> : null}
                </td>
                <td>{s.valve ?? ""}</td>
                <td className="muted">
                  {[s.rs_no && `RS ${s.rs_no}`, s.notif_no && `Notif ${s.notif_no}`, s.func_loc && `FL ${s.func_loc}`, s.equip_no && `Eq ${s.equip_no}`].filter(Boolean).join(" · ")}
                </td>
                <td>
                  {s.leg_km} km · {s.leg_duration}
                </td>
                <td>{s.arrive_clock}</td>
                <td>{s.depart_clock}</td>
                {sheet.has_actuals ? (
                  <>
                    <td>{s.actual_arrive_clock ?? "—"}</td>
                    <td>{s.actual_depart_clock ?? "—"}</td>
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sheet-map">
          {png ? <img src={png} alt={`Map of day ${sheet.day_number}`} /> : <div className="sheet-map-empty dashed-box">Map</div>}
          {sheet.approximate_line ? <p>Dashed line: stop-to-stop, not the road.</p> : null}
        </div>
      </div>
      {sheet.check_on_site.length > 0 ? (
        <section className="sheet-checks">
          <h3>Check on site</h3>
          <ul>
            {sheet.check_on_site.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}
